import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "@/state/hooks";
import {
  applySignalAccount,
  applySignalOrders,
  applySignalPositions,
  applySignalSnapshot,
  applySignalStatus,
  fetchSignalStatus,
  setSignalStreamConnected,
} from "@/state/slices/signalSlice";
import type {
  TradingAccountResponse,
  TradingOrdersResponse,
  TradingPositionsResponse,
} from "@/api/types";
import { apiUrl } from "@/api/client";

/** Subscribe to `/api/signal/stream` (SSE) for the shared read-only `bot_all`
 *  reference — account / positions / orders + a `status` (online/offline) event.
 *
 *  Unlike `useTradingStream` (owner-only, the user's OWN account), this is open
 *  to EVERY signed-in user — it's a public reference. It only connects once the
 *  status probe reports the feature `enabled`, so disabled envs (dev) never open
 *  a needless EventSource. EventSource auto-reconnects on drops; when the source
 *  is offline the backend holds the stream and ends it once bot_all comes up, so
 *  the browser reconnects into the live relay. */
export function useSignalStream(): void {
  const dispatch = useAppDispatch();
  const meStatus = useAppSelector((s) => s.me.status);
  const enabled = useAppSelector((s) => s.signal.enabled);

  // Probe status once signed in so we know whether to stream at all.
  useEffect(() => {
    if (meStatus === "succeeded") {
      dispatch(fetchSignalStatus());
    }
  }, [dispatch, meStatus]);

  useEffect(() => {
    if (meStatus !== "succeeded" || !enabled) return;

    const source = new EventSource(apiUrl("/api/signal/stream"), {
      withCredentials: true,
    });

    source.addEventListener("open", () => {
      dispatch(setSignalStreamConnected(true));
    });

    source.addEventListener("status", (e: MessageEvent) => {
      try {
        dispatch(applySignalStatus(JSON.parse(e.data)));
      } catch {
        /* ignore malformed status frame */
      }
    });

    source.addEventListener("snapshot", (e: MessageEvent) => {
      try {
        dispatch(applySignalSnapshot(JSON.parse(e.data)));
      } catch {
        /* ignore */
      }
    });

    source.addEventListener("account", (e: MessageEvent) => {
      try {
        const payload: TradingAccountResponse = JSON.parse(e.data);
        dispatch(applySignalAccount(payload));
      } catch {
        /* ignore */
      }
    });

    source.addEventListener("positions", (e: MessageEvent) => {
      try {
        const payload: TradingPositionsResponse = JSON.parse(e.data);
        dispatch(applySignalPositions(payload));
      } catch {
        /* ignore */
      }
    });

    source.addEventListener("orders", (e: MessageEvent) => {
      try {
        const payload: TradingOrdersResponse = JSON.parse(e.data);
        dispatch(applySignalOrders(payload));
      } catch {
        /* ignore */
      }
    });

    source.addEventListener("error", () => {
      dispatch(setSignalStreamConnected(false));
    });

    return () => {
      source.close();
      dispatch(setSignalStreamConnected(false));
    };
  }, [dispatch, meStatus, enabled]);
}
